import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const EmployeeManagementModule = () => {
  const [activeSection, setActiveSection] = useState("employee-database");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleEmployeeMgmt = () => navigate('/access-recruit');

  const sectionContent = {
    "employee-database": {
      title: "Centralized Employee Data",
      description: "Maintain a single source of truth for all employee information with our comprehensive HR database. Store personal details, employment history, qualifications, certifications, and contact information in secure, organized profiles. Track employment milestones, anniversary dates, and career progression. Ensure data accuracy with automated validation and provide role-based access controls for different HR functions and management levels."
    },
    "org-chart": {
      title: "Dynamic Organizational Charts",
      description: "Visualize your organizational structure with interactive, real-time org charts. Create hierarchical views by department, location, or business unit. Track reporting lines, matrix relationships, and team compositions. Update structures dynamically as teams grow or reorganize. Provide employees with visibility into the organization while maintaining privacy controls for sensitive information and reporting relationships."
    },
    "attendance-leave": {
      title: "Attendance & Leave System",
      description: "Streamline time tracking and leave management with automated attendance systems. Implement flexible timekeeping options including biometric, mobile check-ins, and remote tracking. Manage various leave types including vacation, sick leave, parental leave, and personal time off. Automate approval workflows, balance calculations, and policy compliance. Generate detailed attendance reports and analytics for workforce planning and compliance requirements."
    },
    "performance-management": {
      title: "Performance & Goal Tracking",
      description: "Drive employee development with structured performance management systems. Set SMART goals, track progress, and conduct regular performance reviews. Implement 360-degree feedback mechanisms, competency assessments, and development planning. Align individual objectives with organizational goals and provide managers with tools for continuous coaching and feedback throughout the performance cycle."
    },
    "compensation-benefits": {
      title: "Benefits & Administration",
      description: "Manage comprehensive compensation structures and benefits programs efficiently. Administer salary bands, bonuses, incentives, and equity plans. Track benefits enrollment, claims, and provider management. Ensure compliance with compensation regulations and conduct pay equity analyses. Provide employees with self-service access to compensation statements and benefits information while maintaining confidentiality and security controls."
    }
  };

  const sections = [
    { id: "employee-database", label: "EMPLOYEE DB" },
    { id: "org-chart", label: "ORG CHART" },
    { id: "attendance-leave", label: "ATTENDANCE" },
    { id: "performance-management", label: "PERFORMANCE" },
    { id: "compensation-benefits", label: "COMPENSATION" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Employee Database */}
        <section
          ref={(el) => (sectionsRef.current["employee-database"] = el)}
          data-section="employee-database"
          className={`resto-section ${visibleSections.has("employee-database") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Employee Database</div>
            <h2 className="resto-section-title">
              {sectionContent["employee-database"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["employee-database"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleEmployeeMgmt}>View Database</button>
              <button className="resto-secondary-btn" onClick={handleEmployeeMgmt}>Update Profiles</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/epl1.png" className="resto-real-image" alt="Employee Database" />
          </div>
        </section>

        {/* 2. Organizational Chart */}
        <section
          ref={(el) => (sectionsRef.current["org-chart"] = el)}
          data-section="org-chart"
          className={`resto-section resto-reverse ${visibleSections.has("org-chart") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Organization</div>
            <h2 className="resto-section-title">
              {sectionContent["org-chart"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["org-chart"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleEmployeeMgmt}>View Org Chart</button>
              <button className="resto-secondary-btn" onClick={handleEmployeeMgmt}>Update Structure</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/epl2.png" className="resto-real-image" alt="Org Chart" />
          </div>
        </section>

        <CTA/>

        {/* 3. Attendance & Leave Management */}
        <section
          ref={(el) => (sectionsRef.current["attendance-leave"] = el)}
          data-section="attendance-leave"
          className={`resto-section ${visibleSections.has("attendance-leave") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Time Management</div>
            <h2 className="resto-section-title">
              {sectionContent["attendance-leave"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["attendance-leave"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleEmployeeMgmt}>Track Attendance</button>
              <button className="resto-secondary-btn" onClick={handleEmployeeMgmt}>Manage Leaves</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/epl3.png" className="resto-real-image" alt="Attendance" />
          </div>
        </section>

        {/* 4. Performance Management */}
        <section
          ref={(el) => (sectionsRef.current["performance-management"] = el)}
          data-section="performance-management"
          className={`resto-section resto-reverse ${visibleSections.has("performance-management") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Performance</div>
            <h2 className="resto-section-title">
              {sectionContent["performance-management"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["performance-management"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleEmployeeMgmt}>Set Goals</button>
              <button className="resto-secondary-btn" onClick={handleEmployeeMgmt}>Conduct Reviews</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/epl4.png" className="resto-real-image" alt="Performance" />
          </div>
        </section>

        {/* 5. Compensation & Benefits */}
        <section
          ref={(el) => (sectionsRef.current["compensation-benefits"] = el)}
          data-section="compensation-benefits"
          className={`resto-section ${visibleSections.has("compensation-benefits") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Compensation</div>
            <h2 className="resto-section-title">
              {sectionContent["compensation-benefits"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["compensation-benefits"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleEmployeeMgmt}>Manage Payroll</button>
              <button className="resto-secondary-btn" onClick={handleEmployeeMgmt}>Admin Benefits</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/epl5.png" className="resto-real-image" alt="Compensation" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default EmployeeManagementModule;